import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShoppingCart, Package, Download, Cpu } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/context/CartContext";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const TYPE_META = {
  physical: { icon: Package, de: "Physisch", en: "Physical" },
  digital: { icon: Download, de: "Digital", en: "Digital" },
  software: { icon: Cpu, de: "Software", en: "Software" },
};

export function priceOf(p) { return Number(p.sale_price || p.price || 0); }
export function fmt(n) { return Number(n || 0).toFixed(2).replace(".", ",") + " €"; }
export function isFree(p) { return priceOf(p) <= 0; }

export function ProductCard({ p, i = 0 }) {
  const { lang, pickL } = useI18n();
  const { add } = useCart();
  const de = lang === "de";
  const meta = TYPE_META[p.type] || TYPE_META.digital;
  const Icon = meta.icon;
  const onSale = p.sale_price && p.sale_price < p.price;
  const free = isFree(p);
  return (
    <Reveal delay={i * 0.04}>
      <div className="surface overflow-hidden h-full flex flex-col group" data-testid={`product-card-${p.slug}`}>
        <Link to={`/shop/${p.slug}`} className="block relative aspect-[4/3] bg-secondary overflow-hidden">
          {(p.images || [])[0]
            ? <img src={p.images[0]} alt={pickL(p, "name")} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><Icon size={44} /></div>}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-background/90 backdrop-blur px-2.5 py-1 text-[11px] font-semibold">
            <Icon size={12} className="text-primary" /> {de ? meta.de : meta.en}
          </span>
          {free ? <span className="absolute top-3 right-3 rounded-full bg-green-500 text-white px-2.5 py-1 text-[11px] font-bold">{de ? "Kostenlos" : "Free"}</span>
            : onSale && <span className="absolute top-3 right-3 rounded-full bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-bold">Sale</span>}
        </Link>
        <div className="p-5 flex flex-col flex-1">
          <Link to={`/shop/${p.slug}`}><h3 className="font-display font-bold text-lg leading-tight hover:text-primary transition-colors">{pickL(p, "name")}</h3></Link>
          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 flex-1">{pickL(p, "short")}</p>
          <div className="mt-4 flex items-end justify-between gap-3">
            <div>
              {free ? (
                <span className="font-display text-2xl font-black text-green-500">{de ? "Kostenlos" : "Free"}</span>
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    {onSale && <span className="text-sm text-muted-foreground line-through">{fmt(p.price)}</span>}
                    <span className="font-display text-2xl font-black">{fmt(priceOf(p))}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."}</div>
                </>
              )}
            </div>
            {free ? (
              <Link to={`/shop/${p.slug}`} className="btn-primary !py-2 !px-4 text-sm" data-testid={`product-free-${p.slug}`}>
                <Download size={15} /> {de ? "Kostenlos erhalten" : "Get free"}
              </Link>
            ) : (
              <button onClick={() => { add(p); toast.success(de ? "Zum Warenkorb hinzugefügt" : "Added to cart"); }} className="btn-primary !py-2 !px-4 text-sm" data-testid={`product-add-${p.slug}`}>
                <ShoppingCart size={15} /> {de ? "In den Warenkorb" : "Add"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Shop() {
  const { t, lang } = useI18n();
  const de = lang === "de";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [ptype, setPtype] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/shop/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/shop/products", { params: { category: cat, ptype, q, sort } })
      .then((r) => setProducts(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [cat, ptype, q, sort]);

  const types = [["", de ? "Alle" : "All"], ["physical", de ? "Physisch" : "Physical"], ["digital", "Digital"], ["software", "Software"]];

  return (
    <div>
      <Seo title={de ? "Shop" : "Shop"} description={de ? "Digitale Produkte, Software, Vorlagen und mehr." : "Digital products, software, templates and more."} />
      <PageHero eyebrow="Shop" title={de ? "Unser Shop" : "Our Shop"} subtitle={de ? "Digitale Produkte, Software-Lizenzen und Vorlagen — sofort verfügbar." : "Digital products, software licenses and templates — instantly available."} />
      <section className="container-x py-12">
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={de ? "Produkte suchen…" : "Search products…"}
              data-testid="shop-search" className="w-full rounded-lg border border-border bg-card pl-10 pr-4 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort" className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary">
            <option value="new">{de ? "Neueste" : "Newest"}</option>
            <option value="featured">{de ? "Empfohlen" : "Featured"}</option>
            <option value="price_asc">{de ? "Preis aufsteigend" : "Price ↑"}</option>
            <option value="price_desc">{de ? "Preis absteigend" : "Price ↓"}</option>
            <option value="name">{de ? "Name" : "Name"}</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {types.map(([k, l]) => (
            <button key={k} onClick={() => setPtype(k)} data-testid={`shop-type-${k || "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${ptype === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{l}</button>
          ))}
        </div>

        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            <button onClick={() => setCat("")} className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${!cat ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{de ? "Alle Kategorien" : "All categories"}</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`shop-cat-${c.slug}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${cat === c.id ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                {de ? c.name_de : (c.name_en || c.name_de)}{typeof c.count === "number" ? ` (${c.count})` : ""}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="surface aspect-[4/3] animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <p className="text-muted-foreground surface p-12 text-center" data-testid="shop-empty">{de ? "Keine Produkte gefunden." : "No products found."}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
            {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
